import React, { useState, useEffect, useRef } from "react";
import { useSwipeable } from "react-swipeable";
import InterestPointsPreview from "./InterestPointsPreview";

const SwipeableInteresPoints = ({ anchorRef, generalStyles, buttonStyles, points, active, loading }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [collapsedPx, setCollapsedPx] = useState(window.innerHeight * 0.7);
    const [dragTop, setDragTop] = useState(null);
    const [anchorRect, setAnchorRect] = useState({
        left: 0,
        width: window.innerWidth,
        height: 0,
    });
    const [fixedHeight, setFixedHeight] = useState(null);
    const [headerHeight, setHeaderHeight] = useState(0);
    const [tabSliderHeight, setTabSliderHeight] = useState(0);
    const [isReady, setIsReady] = useState(false);
    const [allowScroll, setAllowScroll] = useState(true);
    const [hasFixedHeight, setHasFixedHeight] = useState(false);

    const previewRef = useRef(null);
    const handleRef = useRef(null);

    const expandedPx = headerHeight;

    useEffect(() => {
        const el = previewRef.current;
        if (!el) return;

        let startY = 0;

        const onTouchStart = (e) => {
            startY = e.touches[0].clientY;
        };

        const onTouchMove = (e) => {
            const currentY = e.touches[0].clientY;
            const diffY = currentY - startY;

            if (diffY > 0 && el.scrollTop === 0) {
                e.preventDefault();
            }
        };

        el.addEventListener("touchstart", onTouchStart, { passive: false });
        el.addEventListener("touchmove", onTouchMove, { passive: false });

        return () => {
            el.removeEventListener("touchstart", onTouchStart);
            el.removeEventListener("touchmove", onTouchMove);
        };
    }, []);

    useEffect(() => {
        const update = () => {
            if (anchorRef?.current) {
                const rect = anchorRef.current.getBoundingClientRect();
                setCollapsedPx(rect.bottom);
                setAnchorRect({
                    left: rect.left,
                    width: rect.width,
                    height: rect.height,
                });
            }
        };

        const header = document.querySelector("#topbar");
        if (header) {
            setHeaderHeight(header.getBoundingClientRect().height);
        }

        const tabSlider = document.querySelector("#tab-slider");
        if (tabSlider) {
            setTabSliderHeight(tabSlider.getBoundingClientRect().height);
        }

        let timeoutId;
        if (!loading) {
            timeoutId = setTimeout(() => {
                update();
                setIsReady(true);
            }, 500);
        }

        window.addEventListener("resize", update);
        window.visualViewport?.addEventListener("resize", update);
        window.visualViewport?.addEventListener("scroll", update);
        return () => {
            window.removeEventListener("resize", update);
            window.visualViewport?.removeEventListener("resize", update);
            window.visualViewport?.removeEventListener("scroll", update);
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [anchorRef, loading]);

    useEffect(() => {
        const el = previewRef.current;
        if (!el) return;

        const handleScroll = () => {
            if (el.scrollTop === 0) {
                setAllowScroll(true);
            } else if (!allowScroll) {
                el.scrollTop = 0;
            }
        };

        const preventTouch = (e) => {
            if (!allowScroll) e.preventDefault();
        };

        el.addEventListener("scroll", handleScroll);
        el.addEventListener("touchmove", preventTouch, { passive: false });

        return () => {
            el.removeEventListener("scroll", handleScroll);
            el.removeEventListener("touchmove", preventTouch);
        };
    }, [allowScroll]);

    useEffect(() => {
        const el = previewRef.current;
        if (!el) return;

        const preventTouchScroll = (e) => {
            if (!isExpanded) {
                e.preventDefault();
            }
        };

        el.addEventListener("touchmove", preventTouchScroll, { passive: false });

        return () => {
            el.removeEventListener("touchmove", preventTouchScroll);
        };
    }, [isExpanded]);

    const handlers = useSwipeable({
        onSwiping: ({ deltaY, event }) => {
            if (!hasFixedHeight && deltaY < 0) return;
            const fromHandle = handleRef.current?.contains(event.target);
            const preview = previewRef.current;

            if (!fromHandle && isExpanded && preview) {
                if (deltaY < 0 && preview.scrollTop + preview.clientHeight < preview.scrollHeight) {
                    return;
                }
                if (deltaY > 0 && preview.scrollTop > 0) {
                    return;
                }
            }

            if (fromHandle && preview) {
                preview.scrollTop = 0;
            }

            const start = isExpanded ? expandedPx : collapsedPx;
            let newTop = start + deltaY;
            newTop = Math.min(Math.max(newTop, expandedPx), collapsedPx);
            setDragTop(newTop);
        },

        onSwipedUp: () => {
            if (!hasFixedHeight) return;

            setIsExpanded(true);
            setDragTop(null);
        },

        onSwipedDown: ({ event, velocity, deltaY }) => {
            const fromHandle = handleRef.current?.contains(event.target);
            const preview = previewRef.current;

            const SCROLL_THRESHOLD = 10;

            if (fromHandle && preview) {
                preview.scrollTop = 0;
            }

            if (preview?.scrollTop > SCROLL_THRESHOLD && !fromHandle) return;

            const shouldCollapse = velocity > 0.3 || deltaY > 50 || (deltaY > 20 && !isExpanded);

            if (shouldCollapse) {
                setIsExpanded(false);
                setDragTop(null);
            }
        },

        trackTouch: true,
        trackMouse: true,
        preventScrollOnSwipe: false,
    });

    const currentTop = dragTop !== null ? dragTop : isExpanded ? expandedPx : collapsedPx;
    const roundedTop = Math.round(currentTop);

    return (
        <div
            {...handlers}
            style={{
                position: "fixed",
                left: `${anchorRect.left}px`,
                width: `${anchorRect.width}px`,
                height: `calc(var(--vh, 1vh) * 100 - ${headerHeight}px)`,
                background: "#fff",
                zIndex: 30,
                transform: `translateY(${roundedTop}px)`,
                transition: dragTop === null ? "transform 0.3s ease-in-out" : "none",
                willChange: "transform",
                overscrollBehavior: "contain",
                touchAction: "pan-y",
                cursor: !hasFixedHeight ? "default" : "grab",
                opacity: isReady ? 1 : 0,
                transitionProperty: "transform, opacity",
                transitionDuration: "0.3s",
                transitionTimingFunction: "ease-in-out",
                display: "flex",
                alignItems: hasFixedHeight ? "center" : "flex-start",
                justifyContent: hasFixedHeight ? "center" : "flex-start",
                minHeight: 0,
            }}
        >
            <div
                className="h-8"
                ref={handleRef}
                style={{
                    width: "100%",
                    top: "0rem",
                    left: "50%",
                    position: "absolute",
                    // backgroundColor: generalStyles?.bgColor,
                    transform: "translateX(-50%)",
                    display: "flex",
                    justifyContent: "center",
                    opacity: !hasFixedHeight ? "0" : "100",
                    zIndex: 90,
                }}
            >
                <div
                    style={{
                        width: "6rem",
                        height: "0.25rem",
                        backgroundColor: generalStyles?.detail,
                        borderRadius: "0.125rem",
                        cursor: "grab",
                        alignSelf: "center",
                    }}
                />
            </div>

            <div
                ref={previewRef}
                className=" px-4 py-10"
                style={{
                    position: "relative",
                    width: "100%",
                    height: hasFixedHeight ? "100%" : `calc(100% - ${anchorRect.height}px - ${tabSliderHeight}px)`,
                    overflowY: "auto",
                    backgroundColor: generalStyles?.bgColor || "#fff",
                    WebkitOverflowScrolling: "touch",
                }}
            >
                <InterestPointsPreview
                    points={points}
                    active={active}
                    generalStyles={generalStyles}
                    buttonStyles={buttonStyles}
                    setHasFixedHeight={setHasFixedHeight}
                    hasFixedHeight={hasFixedHeight}
                />
            </div>
        </div>
    );
};

export default SwipeableInteresPoints;
